import { create } from 'zustand';
import { fetchMoneySavers, MoneySaversResponse } from '../services/analysis/analysisService';
import type { WasteActionItem, WasteFinding } from '../utils/analysis/wasteActionPayload';

/** Reviewed findings survive navigation only; v1 intentionally has no persistence. */
export type FoodDrinksFilter = 'all' | 'food' | 'drinks';

interface AnalysisState {
  wasteFindings: Record<string, WasteFinding[]>;
  analysisFindingsSeen: boolean;
  foodDrinksFilter: FoodDrinksFilter;
  moneySaversBySelection: Record<string, MoneySaversResponse>;
  moneySaversLoading: Record<string, boolean>;
  moneySaversError: Record<string, string | null>;
  saveWasteFindings: (restaurantId: string, findings: WasteFinding[]) => void;
  /** Super admin edits on /analysis/waste; the finding's numbers stay as saved. */
  setWasteFindingActionItems: (restaurantId: string, groupId: string, actionItems: WasteActionItem[]) => void;
  markAnalysisFindingsSeen: () => void;
  setFoodDrinksFilter: (value: FoodDrinksFilter) => void;
  loadMoneySavers: (restaurantSelection: string) => Promise<void>;
  clear: () => void;
}

// Invalidates requests started by a previous signed-in actor.
let generation = 0;

export const useAnalysisStore = create<AnalysisState>((set, get) => ({
  wasteFindings: {},
  analysisFindingsSeen: true,
  foodDrinksFilter: 'all',
  moneySaversBySelection: {},
  moneySaversLoading: {},
  moneySaversError: {},

  clear: () => {
    generation++;
    set({
      wasteFindings: {},
      analysisFindingsSeen: true,
      foodDrinksFilter: 'all',
      moneySaversBySelection: {},
      moneySaversLoading: {},
      moneySaversError: {},
    });
  },
  saveWasteFindings: (restaurantId, findings) => set(state => {
    if (!findings.length) return state;
    const byGroup = new Map((state.wasteFindings[restaurantId] ?? []).map(f => [f.groupId, f]));
    findings.forEach(finding => byGroup.set(finding.groupId, { ...finding, restaurantId }));
    return {
      wasteFindings: { ...state.wasteFindings, [restaurantId]: Array.from(byGroup.values()) },
      analysisFindingsSeen: false,
    };
  }),
  setWasteFindingActionItems: (restaurantId, groupId, actionItems) => set(state => ({
    wasteFindings: {
      ...state.wasteFindings,
      [restaurantId]: (state.wasteFindings[restaurantId] ?? []).map(f => f.groupId === groupId ? { ...f, actionItems } : f),
    },
  })),
  markAnalysisFindingsSeen: () => set({ analysisFindingsSeen: true }),
  setFoodDrinksFilter: (value) => set({ foodDrinksFilter: value }),

  loadMoneySavers: async (restaurantSelection) => {
    const state = get();
    const requestGeneration = generation;
    if (state.moneySaversBySelection[restaurantSelection] || state.moneySaversLoading[restaurantSelection]) return;
    set(s => ({
      moneySaversLoading: { ...s.moneySaversLoading, [restaurantSelection]: true },
      moneySaversError: { ...s.moneySaversError, [restaurantSelection]: null },
    }));
    try {
      const data = await fetchMoneySavers(restaurantSelection);
      if (requestGeneration !== generation) return;
      set(s => ({
        moneySaversBySelection: { ...s.moneySaversBySelection, [restaurantSelection]: data },
        moneySaversLoading: { ...s.moneySaversLoading, [restaurantSelection]: false },
      }));
    } catch (error) {
      if (requestGeneration !== generation) return;
      console.error('Failed to load money savers:', error);
      set(s => ({
        moneySaversLoading: { ...s.moneySaversLoading, [restaurantSelection]: false },
        moneySaversError: {
          ...s.moneySaversError,
          [restaurantSelection]: error instanceof Error ? error.message : 'Failed to load money savers',
        },
      }));
    }
  },

}));
